import { useQuery } from "@apollo/client";
import { DateTime, Settings } from "luxon";
import { useEffect, useState } from "react";
import { WORKERS_QUERY } from "@/graphql/workers";
import { useSalonStore } from "@/lib/store/salon";

interface UseSchedulingStepProps {
  prefilledData?: {
    workerId?: string;
    startTime?: Date;
    date?: string;
    time?: string;
    notes?: string;
  };
  onScheduleChange?: (data: {
    date: DateTime | undefined;
    time: string;
    workerId: string;
    notes: string;
  }) => void;
}

export function useSchedulingStep({
  prefilledData,
  onScheduleChange,
}: UseSchedulingStepProps = {}) {
  const { currentSalon } = useSalonStore();

  // Scheduling state
  const [date, setDate] = useState<DateTime | undefined>(
    prefilledData?.startTime
      ? DateTime.fromJSDate(prefilledData.startTime)
      : prefilledData?.date
        ? DateTime.fromFormat(prefilledData.date, "yyyy-MM-dd")
        : DateTime.now()
  );

  const [time, setTime] = useState(
    prefilledData?.startTime
      ? DateTime.fromJSDate(prefilledData.startTime).toFormat("HH:mm")
      : prefilledData?.time || ""
  );

  const [workerId, setWorkerId] = useState<string>(prefilledData?.workerId || "unassigned");

  const [notes, setNotes] = useState(prefilledData?.notes || "");

  // Workers query
  const {
    data: workersData,
    loading: workersLoading,
    error: workersError,
  } = useQuery(WORKERS_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      filters: { isActive: true },
    },
    skip: !currentSalon?.id,
  });

  // Handle date change
  const handleDateChange = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const salonDateTime = DateTime.fromObject(
        {
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate(),
        },
        { zone: Settings.defaultZone.name }
      ).startOf("day");
      setDate(salonDateTime);
    } else {
      setDate(undefined);
    }
  };

  // Handle time change with auto-formatting
  const handleTimeChange = (value: string) => {
    let formattedValue = value.replace(/[^\d:]/g, "");

    // Auto-format as user types
    if (formattedValue.length === 2 && !formattedValue.includes(":")) {
      formattedValue = `${formattedValue}:`;
    }

    // Limit to HH:MM format
    if (formattedValue.length > 5) {
      formattedValue = formattedValue.slice(0, 5);
    }

    // Validate and set
    const timeRegex =
      /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$|^([0-1]?[0-9]|2[0-3]):?$|^([0-1]?[0-9]|2[0-3]):[0-5]?$/;
    if (formattedValue === "" || timeRegex.test(formattedValue)) {
      setTime(formattedValue);
    }
  };

  // Handle time blur (final formatting)
  const handleTimeBlur = (value: string) => {
    let formattedValue = value;
    if (value.length === 4 && value.includes(":")) {
      const [hours, minutes] = value.split(":");
      if (hours && minutes && minutes.length === 1) {
        formattedValue = `${hours.padStart(2, "0")}:${minutes}0`;
      }
    } else if (value.length === 5) {
      const [hours, minutes] = value.split(":");
      if (hours && minutes) {
        formattedValue = `${hours.padStart(2, "0")}:${minutes}`;
      }
    }
    setTime(formattedValue);
  };

  // Check if date is disabled
  const isDateDisabled = (jsDate: Date) => {
    const today = DateTime.now().startOf("day");
    const checkDate = DateTime.fromObject(
      {
        year: jsDate.getFullYear(),
        month: jsDate.getMonth() + 1,
        day: jsDate.getDate(),
      },
      { zone: Settings.defaultZone.name }
    ).startOf("day");
    return checkDate < today;
  };

  // Get start and end times
  const getStartEndTimes = (totalDuration: number) => {
    if (!date || !time) return null;

    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    const startTime = date.set({
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    });

    const endTime = startTime.plus({ minutes: totalDuration });

    return { startTime, endTime };
  };

  // Reset scheduling data
  const resetScheduling = () => {
    setDate(DateTime.now());
    setTime("");
    setWorkerId("unassigned");
    setNotes("");
  };

  // Validation
  const isValid = () => {
    return !!(date && time);
  };

  // Validate time format
  const isTimeValid = () => {
    if (!time) return false;
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  // Update when prefilled data changes
  useEffect(() => {
    if (prefilledData) {
      setDate(
        prefilledData.startTime
          ? DateTime.fromJSDate(prefilledData.startTime)
          : prefilledData.date
            ? DateTime.fromFormat(prefilledData.date, "yyyy-MM-dd")
            : DateTime.now()
      );
      setTime(
        prefilledData.startTime
          ? DateTime.fromJSDate(prefilledData.startTime).toFormat("HH:mm")
          : prefilledData.time || ""
      );
      setWorkerId(prefilledData.workerId || "unassigned");
      setNotes(prefilledData.notes || "");
    }
  }, [prefilledData]);

  // Notify parent of changes
  useEffect(() => {
    onScheduleChange?.({ date, time, workerId, notes });
  }, [date, time, workerId, notes, onScheduleChange]);

  return {
    // State
    date,
    setDate,
    time,
    setTime,
    workerId,
    setWorkerId,
    notes,
    setNotes,

    // Data
    workersData,

    // Actions
    handleDateChange,
    handleTimeChange,
    handleTimeBlur,
    isDateDisabled,
    getStartEndTimes,
    resetScheduling,

    // Validation
    isValid,
    isTimeValid,

    // Status
    loading: workersLoading,
    error: workersError,
  };
}
